import { useCallback, useState } from "react";

/**
 * Per-browser display preferences of the UDP pipeline panel on the Metrics page.
 * Purely local UI state: never sent to the backend, never shared between viewers.
 *
 * Stored as an explicit visibility map. A section id that is missing from the
 * stored map (a section added after the browser saved its prefs) is visible, so a
 * new section is never hidden by an old save. Anything unreadable → defaults.
 */

export const UDP_PIPELINE_PREFS_KEY = "voltie.metrics.udpPipelinePanel.v1";

/** Sections by stored id; `labelKey` is the settings checkbox label in the catalog. */
export const UDP_PIPELINE_SECTIONS = [
  { id: "queue", labelKey: "operator.ops.udp.section_queue" },
  { id: "redis", labelKey: "operator.ops.udp.section_redis" },
  { id: "sampleProjection", labelKey: "operator.ops.udp.section_sample_projection" },
  { id: "flusherStages", labelKey: "operator.ops.udp.section_flusher_stages" },
  { id: "hotPath", labelKey: "operator.ops.udp.section_hot_path" },
  { id: "backgroundPressure", labelKey: "operator.ops.udp.section_background_pressure" },
  { id: "sessionProcessor", labelKey: "operator.ops.udp.section_session_processor" },
  { id: "gateEval", labelKey: "operator.ops.udp.section_gate_eval" },
] as const;

export type UdpPipelineSectionId = (typeof UDP_PIPELINE_SECTIONS)[number]["id"];

/** Hot-path stages with a collapsible child breakdown; closed unless opened here. */
export const UDP_PIPELINE_BREAKDOWNS = ["unit_update", "unit_lifecycle", "command"] as const;
export type UdpPipelineBreakdownId = (typeof UDP_PIPELINE_BREAKDOWNS)[number];

export interface UdpPipelinePanelPrefs {
  collapsed: boolean;
  sections: Record<UdpPipelineSectionId, boolean>;
  /** Open breakdowns. Missing from an older save → closed. */
  breakdowns: Record<UdpPipelineBreakdownId, boolean>;
}

export function defaultUdpPipelinePrefs(): UdpPipelinePanelPrefs {
  const sections = {} as Record<UdpPipelineSectionId, boolean>;
  for (const s of UDP_PIPELINE_SECTIONS) sections[s.id] = true;
  const breakdowns = {} as Record<UdpPipelineBreakdownId, boolean>;
  for (const id of UDP_PIPELINE_BREAKDOWNS) breakdowns[id] = false;
  return { collapsed: false, sections, breakdowns };
}

/** Stored text → prefs. Unknown or damaged fields fall back to their default. */
export function parseUdpPipelinePrefs(raw: string | null): UdpPipelinePanelPrefs {
  const prefs = defaultUdpPipelinePrefs();
  if (raw === null) return prefs;
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    return prefs;
  }
  if (data === null || typeof data !== "object" || Array.isArray(data)) return prefs;
  const obj = data as Record<string, unknown>;
  if (typeof obj.collapsed === "boolean") prefs.collapsed = obj.collapsed;
  const stored = obj.sections;
  if (stored !== null && typeof stored === "object" && !Array.isArray(stored)) {
    for (const s of UDP_PIPELINE_SECTIONS) {
      const v = (stored as Record<string, unknown>)[s.id];
      if (typeof v === "boolean") prefs.sections[s.id] = v;
    }
  }
  const open = obj.breakdowns;
  if (open !== null && typeof open === "object" && !Array.isArray(open)) {
    for (const id of UDP_PIPELINE_BREAKDOWNS) {
      const v = (open as Record<string, unknown>)[id];
      if (typeof v === "boolean") prefs.breakdowns[id] = v;
    }
  }
  return prefs;
}

export function loadUdpPipelinePrefs(): UdpPipelinePanelPrefs {
  try {
    return parseUdpPipelinePrefs(localStorage.getItem(UDP_PIPELINE_PREFS_KEY));
  } catch {
    return defaultUdpPipelinePrefs();
  }
}

function saveUdpPipelinePrefs(prefs: UdpPipelinePanelPrefs): void {
  try {
    localStorage.setItem(UDP_PIPELINE_PREFS_KEY, JSON.stringify(prefs));
  } catch {
    // Storage blocked or full: the choice still applies until the page reloads.
  }
}

export function useUdpPipelinePrefs() {
  const [prefs, setPrefs] = useState<UdpPipelinePanelPrefs>(loadUdpPipelinePrefs);

  const update = useCallback((next: (prev: UdpPipelinePanelPrefs) => UdpPipelinePanelPrefs) => {
    setPrefs((prev) => {
      const value = next(prev);
      saveUdpPipelinePrefs(value);
      return value;
    });
  }, []);

  const setCollapsed = useCallback(
    (collapsed: boolean) => update((p) => ({ ...p, collapsed })),
    [update],
  );
  const setSectionVisible = useCallback(
    (id: UdpPipelineSectionId, visible: boolean) =>
      update((p) => ({ ...p, sections: { ...p.sections, [id]: visible } })),
    [update],
  );
  const setBreakdownOpen = useCallback(
    (id: UdpPipelineBreakdownId, open: boolean) =>
      update((p) => ({ ...p, breakdowns: { ...p.breakdowns, [id]: open } })),
    [update],
  );
  const reset = useCallback(() => update(() => defaultUdpPipelinePrefs()), [update]);

  return { prefs, setCollapsed, setSectionVisible, setBreakdownOpen, reset };
}
